import { Link } from "react-router-dom";
import { Gauge, PencilLine, Shuffle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Pill } from "@/components/ui/pill";
import { ClosingGrid } from "./ClosingGrid";
import { InputDivergence } from "./InputDivergence";
import { IndicatorChart } from "./IndicatorChart";
import { ProductBreakdown } from "./ProductBreakdown";
import { StageKpiRow } from "./StageKpiRow";
import { CharacteristicComparison } from "./CharacteristicComparison";
import { SuspiciousDaysNotice } from "./SuspiciousDaysNotice";
import type {
  ClosingGridMode,
  ReportComparison,
  ReportFilter,
  ReportGroupBy,
  ReportStage,
} from "@/types/production-report.types";

// Spec 083 (T038) — o bloco de UMA etapa: indicadores, saída por produto, gráfico e fechamento.
//
// A tela é de LEITURA (FR-031). Onde o gestor precisaria escrever, ela oferece o caminho: o link
// para o detalhe da linha, que é onde produção, perda e parada são lançadas.

interface Props {
  stage: ReportStage;
  groupBy: ReportGroupBy;
  mode: ClosingGridMode;
  onModeChange: (mode: ClosingGridMode) => void;
  heroId: string | null;
  onHeroChange: (id: string) => void;
  /** A aba do detalhe já ESTÁ na linha; repetir o link ali seria levar o gestor para onde ele está. */
  showLineLink?: boolean;
  /** Spec 084 — o quadro comparativo e o recorte aplicado, quando houver. */
  comparison?: ReportComparison | null;
  filter?: ReportFilter | null;
  /**
   * A posição da etapa na cadeia, quando há cadeia. `null` no recorte por linha, onde o servidor
   * devolve nível 0 para todas e "Etapa 1" sozinha na tela não informaria nada.
   *
   * ⚠️ Vem do NÍVEL TOPOLÓGICO, não da ordem na lista: com fan-in (duas linhas alimentando a
   * mesma terceira) as duas são nível 0 e a tela mostra "Etapa 1" duas vezes. É correto — elas
   * são mesmo a primeira etapa, em paralelo —, mas parece repetição para quem não sabe disso.
   * Risco conhecido e aceito; numerar pela posição no array seria único e mentiria sobre quem
   * alimenta quem.
   */
  stageNumber?: number | null;
}

/**
 * De ONDE os números desta etapa vieram.
 *
 * O servidor resolve isso desde a Spec 083 (`stage.source`) e a tela não mostrava: dois períodos
 * com o mesmo aproveitamento, um medido por contador e outro digitado à mão, desenhavam idênticos.
 * A confiança que se deposita nos dois não é a mesma, e quem lê precisa saber qual está lendo.
 *
 * ⚠️ Tom NEUTRO de propósito. `mixed` é o período que atravessa a instalação dos contadores —
 * informação, não defeito (é o que o contrato diz). Pintar de alarme mandaria consertar o que não
 * está quebrado.
 */
const SOURCE_LABEL = {
  machine: { texto: "Medido por contador", Icone: Gauge },
  run: { texto: "Declarado no apontamento", Icone: PencilLine },
  mixed: { texto: "Contador e apontamento", Icone: Shuffle },
} as const;

function SourceBadge({ source }: { source: ReportStage["source"] }) {
  const { texto, Icone } = SOURCE_LABEL[source];
  return (
    <Pill tone="neutral" icon={<Icone size={11} aria-hidden />}>
      {texto}
    </Pill>
  );
}

export function StageBlock({
  stage,
  groupBy,
  mode,
  onModeChange,
  heroId,
  onHeroChange,
  showLineLink = true,
  comparison = null,
  filter = null,
  stageNumber = null,
}: Props) {
  const hero = stage.indicators.find((i) => i.id === heroId) ?? stage.indicators[0] ?? null;

  return (
    <Card className="space-y-6 p-5">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2.5">
          {stageNumber !== null ? (
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-text-tertiary">
              Etapa {stageNumber}
            </span>
          ) : null}
          <h2 className="text-[15px] font-semibold text-text-primary">{stage.lineName}</h2>
          <SourceBadge source={stage.source} />
        </div>
        <div className="flex items-center gap-3">
          {stage.indicators.length > 1 ? (
            <label className="flex items-center gap-2 text-[11px] text-text-secondary">
              Indicador
              <select
                className="rounded-md border border-border-default bg-bg-surface px-2 py-1 text-[12px]"
                value={hero?.id ?? ""}
                onChange={(e) => onHeroChange(e.target.value)}
              >
                {stage.indicators.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {showLineLink ? (
            <Link
              to={`/lines/${stage.lineId}/detail`}
              className="text-[12px] text-brand-600 underline-offset-2 hover:underline"
            >
              Abrir a linha
            </Link>
          ) : null}
        </div>
      </header>

      <StageKpiRow stage={stage} hero={hero} />

      <SuspiciousDaysNotice indicator={hero} />

      <CharacteristicComparison
        comparison={comparison}
        filter={filter}
        lineId={stage.lineId}
        unit={stage.unit}
        heroId={hero?.id ?? null}
      />

      <InputDivergence input={stage.input} unit={stage.unit} />

      {stage.input.quantity === null && stage.indicators.length > 0 ? (
        <p className="text-[12px] text-text-secondary">
          Nenhum contador mede a entrada desta etapa, então ela não tem aproveitamento próprio. O
          bloco aparece assim que houver um contador de consumo.
        </p>
      ) : null}

      {stage.indicators.length === 0 ? (
        <p className="text-[12px] text-text-secondary">
          Nenhum indicador declarado para esta linha.{" "}
          <Link to="/organizacao" className="text-brand-600 underline-offset-2 hover:underline">
            Declarar um indicador
          </Link>{" "}
          faz o aproveitamento aparecer aqui.
        </p>
      ) : null}

      {/* Não é 50/50: a lista de destinos carrega nome + quantidade + percentual em cada linha e é
          a que trunca primeiro; o gráfico é uma coluna por período e sobrevive estreito. */}
      <div className="grid gap-6 lg:grid-cols-[1.35fr_1fr]">
        <ProductBreakdown stage={stage} />
        <IndicatorChart stage={stage} heroId={hero?.id ?? null} groupBy={groupBy} />
      </div>

      <ClosingGrid
        stage={stage}
        groupBy={groupBy}
        mode={mode}
        onModeChange={onModeChange}
        heroId={hero?.id ?? null}
      />
    </Card>
  );
}
